import { Button, Typography } from "@mui/material";
import React, { useState } from "react";
import { required, SelectInput, useNotify, useRecordContext, useRefresh } from "react-admin";
import FormModal from "server/components/Modals/FormModal";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { useNotifyError } from "utils/useNotifyError";

const ExchangeGroupDialog = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();

  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // FormModal opens unless onOpen returns false - without the groups there is nothing to pick.
  const fetchData = async () => {
    // The dialog unmounts its form on close, but this state does not - without clearing it, a reopened
    // dialog would show the candidates of the group picked last time.
    setStudents([]);
    setSelectedGroup(null);
    if (!record?.studentEducationId) return false;
    // A swap needs a source group - the backend rejects a student without one, so the dialog never opens.
    if (!record?.studyGroup) {
      notify("Студентът няма текуща група. Използвайте „Премести“.", { type: "error" });
      return false;
    }
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudyGroupsSelect({
          course: record?.currentCourse,
          instructionLanguageId: record?.instructionLanguageId,
          studyTypeId: record?.studyTypeId,
          specialityId: record?.specialityId,
          excludeGroups: record?.studyGroup,
        }),
        null,
        "GET",
      );
      // The current group is never a valid target, so the check runs on what the select would show.
      const availableGroups = (data ?? []).filter(group => group.id !== record?.studyGroup);
      if (!availableGroups.length) {
        notify("Няма налични групи за този студент", { type: "warning" });
        return false;
      }
      setGroups(availableGroups);
    } catch (error) {
      notify("Грешка при зареждане на групите", { type: "error" });
      return false;
    }
  };

  const handleGroupChange = async event => {
    const groupId = event.target.value;
    setSelectedGroup(groupId || null);

    if (!groupId) {
      setStudents([]);
      return;
    }
    try {
      const { data } = await dataProvider.customRequest(
        studentRequests.getStudyGroupSwapCandidates({
          studentEducationId: record?.studentEducationId,
          studyGroup: groupId,
        }),
        null,
        "GET",
      );
      // The endpoint returns only id + facultyNumber; names are used when a tenant does send them.
      setStudents(
        (data ?? []).map(student => ({
          id: student.id,
          name:
            [student.firstName, student.secondName, student.lastName].filter(Boolean).join(" ") ||
            `Факултетен № ${student.facultyNumber}`,
        })),
      );
    } catch (err) {
      notifyError(err);
      setStudents([]);
    }
  };

  const handleConfirm = async values => {
    // The target select only renders while there are candidates, so react-hook-form stops validating it -
    // a target left over from a previously picked group would otherwise be submitted.
    if (!students.some(student => student.id === values.targetStudentEducationId)) {
      notify("Изберете студент от избраната група", { type: "error" });
      return false;
    }

    await dataProvider.customRequest(
      studentRequests.swapStudyGroup(),
      {
        sourceStudentEducationId: record.studentEducationId,
        targetStudentEducationId: values.targetStudentEducationId,
      },
      "PUT",
    );
    notify("Успешно заявена размяна", { type: "success" });
    refresh();
  };

  return (
    <FormModal
      onOpen={fetchData}
      titleContent="Размени студент"
      onConfirm={handleConfirm}
      confirmEnabled={students.length > 0}
      triggerButton={<Button startIcon={<SwapHorizontalCircleIcon />}>Размени</Button>}
    >
      <Typography>
        Текуща група на студента: <strong>{`Група ${record.studyGroup}`}</strong>
      </Typography>
      <SelectInput
        label="Избери група"
        source="newGroup"
        choices={groups}
        onChange={handleGroupChange}
        validate={required()}
        emptyText={false}
      />
      {selectedGroup && !students.length && (
        <Typography color="text.secondary">Няма студенти за размяна в избраната група</Typography>
      )}
      {students.length > 0 && (
        <SelectInput
          source="targetStudentEducationId"
          label="Размени със студент"
          choices={students}
          validate={required()}
          emptyText={false}
        />
      )}
    </FormModal>
  );
};

export default ExchangeGroupDialog;
